import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrphanDonationCard } from './beneficiary-profile-components/ProfileBeneficiaryInfo';
import { Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import IconButton from 'components/common/IconButton';
import SoftBadge from 'components/common/SoftBadge';
import StatusBadge from './StatusBadge';
import HafizProgressCard from './hafiz/HafizProgressCard';
import paths from 'routes/paths';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import { parseRouteParams, calculateAge } from 'helpers/utils';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { supabase } from 'lib/supabase';
import { toast } from 'react-toastify';
import { useBeneficiaryStatusLogs } from 'hooks/useBeneficiaries';

// Component to handle document viewing with signed URLs
const DocumentButton = ({ url, index }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      // Extract file path from URL
      // URL format: https://xxx.supabase.co/storage/v1/object/public/documents/{path}
      // or: https://xxx.supabase.co/storage/v1/object/sign/documents/{path}?token=...
      let filePath = '';
      if (url.includes('/storage/v1/object/public/documents/')) {
        filePath = url.split('/storage/v1/object/public/documents/')[1];
      } else if (url.includes('/storage/v1/object/sign/documents/')) {
        filePath = url
          .split('/storage/v1/object/sign/documents/')[1]
          .split('?')[0];
      } else {
        // Try to open directly if format is unknown
        window.open(url, '_blank');
        setLoading(false);
        return;
      }

      // Generate signed URL
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        setLoading(false);
        return;
      }

      // Open in new tab
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
    setLoading(false);
  };

  return (
    <Button
      size="sm"
      variant="outline-secondary"
      className="me-2 mb-1"
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        `Document ${index + 1}`
      )}
    </Button>
  );
};

const InfoRow = ({ label, value, valueClassName = '' }) => (
  <Row className="mb-2">
    <Col sm={4}>
      <span className="text-900" style={{ fontSize: '1rem' }}>
        {label}
      </span>
    </Col>
    <Col sm={8}>
      <span className={valueClassName} style={{ fontSize: '1rem' }}>
        {value || '-'}
      </span>
    </Col>
  </Row>
);

const BeneficiaryProfile = () => {
  const navigate = useNavigate();
  const { beneficiary } = useBeneficiaryContext();

  const formatDate = date => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  // Get sponsorship types from beneficiary data
  const sponsorshipTypes =
    beneficiary.beneficiary_sponsorship_types ||
    beneficiary.sponsorship_types ||
    [];

  // Calculate total monthly cost
  const totalMonthlyCost = sponsorshipTypes.reduce(
    (sum, st) => sum + (Number(st.cost) || 0),
    0
  );

  return (
    <>
      {/* Header Card */}
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <IconButton
            onClick={() => navigate(paths.beneficiaries)}
            icon={'arrow-left'}
            iconClassName="me-2"
            variant="link"
            className="px-0 fs--1 fs-md-0"
          >
            Back To Beneficiary List
          </IconButton>
          <div>
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={() =>
                navigate(
                  parseRouteParams(paths.editBeneficiary, {
                    id: beneficiary.id
                  })
                )
              }
            >
              Edit
            </Button>
            {beneficiary && (
              <PDFDownloadLink
                document={<OrphanDonationCard beneficiary={beneficiary} />}
                fileName={`${beneficiary.first_name}-${beneficiary.last_name}.pdf`}
              >
                {({ loading }) => (
                  <Button>
                    {loading ? 'Loading...' : 'Print Orphan Card'}
                  </Button>
                )}
              </PDFDownloadLink>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Sponsorship Status Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Sponsorship Status
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <div className="mb-2">
                <span
                  className="text-900 d-block mb-1"
                  style={{ fontSize: '1rem' }}
                >
                  Status
                </span>
                {beneficiary.sponsorship_status?.status ? (
                  <StatusBadge
                    pill
                    status={beneficiary.sponsorship_status.status}
                    color={beneficiary.sponsorship_status.color}
                    hidden={Boolean(beneficiary.sponsorship_status.is_hidden)}
                  />
                ) : (
                  <span>-</span>
                )}
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div className="mb-2">
                <span
                  className="text-900 d-block mb-1"
                  style={{ fontSize: '1rem' }}
                >
                  Priority
                </span>
                {beneficiary.sponsorship_priority?.priority_type ? (
                  <SoftBadge
                    pill
                    bg={
                      beneficiary.sponsorship_priority.priority_type
                        .toLowerCase()
                        .includes('urgent') ||
                      beneficiary.sponsorship_priority.priority_type
                        .toLowerCase()
                        .includes('critical')
                        ? 'danger'
                        : beneficiary.sponsorship_priority.priority_type
                            .toLowerCase()
                            .includes('high')
                        ? 'warning'
                        : beneficiary.sponsorship_priority.priority_type
                            .toLowerCase()
                            .includes('normal')
                        ? 'info'
                        : 'success'
                    }
                  >
                    {beneficiary.sponsorship_priority.priority_type}
                  </SoftBadge>
                ) : (
                  <span>-</span>
                )}
              </div>
            </Col>
            <Col xs={12}>
              <InfoRow label="Case Notes" value={beneficiary.case_notes} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Hafiz Quran progress (Hafiz-type children only) */}
      {sponsorshipTypes.some(st =>
        /hafiz|hifz/i.test(st.sponsorship_type?.type || st.type || '')
      ) && (
        <HafizProgressCard
          orphanId={beneficiary.id}
          branchId={beneficiary.branch_id}
        />
      )}

      {/* Profile Information Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Profile Information
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <InfoRow label="First Name" value={beneficiary.first_name} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow label="Middle Name" value={beneficiary.middle_name} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow label="Last Name" value={beneficiary.last_name} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Beneficiary ID"
                value={
                  beneficiary.simple_id ? `B${beneficiary.simple_id}` : '-'
                }
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="HY Connect ID"
                value={beneficiary.hy_connect_id}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Category"
                value={beneficiary.beneficiary_category}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Date of Birth"
                value={formatDate(beneficiary.DOB)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Age"
                value={
                  beneficiary.DOB
                    ? `${calculateAge(beneficiary.DOB)} years`
                    : '-'
                }
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow label="Gender" value={beneficiary.gender} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow label="Main Language" value={beneficiary.language} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Secondary Language"
                value={beneficiary.secondary_language}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Enrollment Date"
                value={formatDate(beneficiary.enrollment_date)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Date Created"
                value={formatDate(beneficiary.created_at)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Inspiration Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Inspiration
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <InfoRow
                label="Career Aspiration"
                value={beneficiary.inspiration}
              />
            </Col>
            <Col md={6}>
              <InfoRow
                label="Interests / Hobbies"
                value={beneficiary.hobbies}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sponsorship Types & Costs Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Sponsorship Types & Costs
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {sponsorshipTypes.length > 0 ? (
            <>
              {sponsorshipTypes.map((st, index) => (
                <Row key={index} className="mb-2 align-items-center">
                  <Col sm={6}>
                    <span>
                      {st.sponsorship_type?.type || st.typeName || '-'}
                    </span>
                  </Col>
                  <Col sm={6}>
                    <span className="text-900" style={{ fontSize: '1rem' }}>
                      Monthly Cost:{' '}
                    </span>
                    <span className="fw-semibold">
                      ${Number(st.cost || 0).toFixed(2)}
                    </span>
                  </Col>
                </Row>
              ))}
              <hr />
              <Row>
                <Col>
                  <strong>Total Monthly Cost: </strong>
                  <span className="text-primary fw-semibold">
                    ${totalMonthlyCost.toFixed(2)}
                  </span>
                </Col>
              </Row>
            </>
          ) : (
            <p className="text-muted mb-0">No sponsorship types assigned</p>
          )}
        </Card.Body>
      </Card>

      {/* Location Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Location
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <InfoRow label="Country" value={beneficiary.country} />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow label="City" value={beneficiary.city} />
            </Col>
            <Col xs={12}>
              <InfoRow label="Address" value={beneficiary.address} />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Health Condition Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Health Condition
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <InfoRow
                label="Condition"
                value={beneficiary.sponsorship_condition?.condition}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Date Assessed"
                value={formatDate(beneficiary.medical_assessed_date)}
              />
            </Col>
            <Col xs={12}>
              <Row className="mb-2">
                <Col sm={4}>
                  <span className="text-900" style={{ fontSize: '1rem' }}>
                    Supporting Documents
                  </span>
                </Col>
                <Col sm={8}>
                  {(() => {
                    const md = beneficiary.medical_documents;
                    let docs = [];
                    if (Array.isArray(md)) {
                      docs = md;
                    } else if (typeof md === 'string' && md) {
                      try {
                        const parsed = JSON.parse(md);
                        docs = Array.isArray(parsed) ? parsed : [md];
                      } catch {
                        docs = [md];
                      }
                    }
                    return docs.length > 0 ? (
                      docs.map((doc, index) => (
                        <DocumentButton key={index} url={doc} index={index} />
                      ))
                    ) : (
                      <span>-</span>
                    );
                  })()}
                </Col>
              </Row>
            </Col>
            <Col xs={12}>
              <InfoRow
                label="Medical Notes"
                value={beneficiary.medical_notes}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Parents Card */}
      {(() => {
        const parents = beneficiary.parents || [];
        const father = parents.find(p => p.parent_type === 'father');
        const mother = parents.find(p => p.parent_type === 'mother');
        if (!father && !mother) return null;
        const renderParent = (label, p) => (
          <>
            <h6 className="mt-2 mb-2">{label}</h6>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <InfoRow label="First Name" value={p?.first_name} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Middle Name" value={p?.middle_name} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Last Name" value={p?.last_name} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Status" value={p?.status} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Contact Number" value={p?.phone} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Contact Email" value={p?.email} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow label="Occupation" value={p?.occupation} />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Monthly Income Range"
                  value={p?.monthly_income_range}
                />
              </Col>
              <Col xs={12}>
                <InfoRow label="Address" value={p?.address} />
              </Col>
            </Row>
          </>
        );
        return (
          <Card className="border shadow-none mb-3">
            <Card.Header className="pb-0">
              <Card.Title as="h5" className="pt-2">
                Parents
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {father && renderParent('Father', father)}
              {father && mother && <hr />}
              {mother && renderParent('Mother', mother)}
            </Card.Body>
          </Card>
        );
      })()}

      {/* Guardian Details Card */}
      <Card className="border shadow-none mb-3">
        <Card.Header className="pb-0">
          <Card.Title as="h5" className="pt-2">
            Current Guardian
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <InfoRow
                label="First Name"
                value={beneficiary.guardian?.first_name}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Middle Name"
                value={beneficiary.guardian?.middle_name}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Last Name"
                value={beneficiary.guardian?.last_name}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Relationship"
                value={beneficiary.guardian?.relationship}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Contact Number"
                value={beneficiary.guardian?.phone}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Contact Email"
                value={beneficiary.guardian?.email}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Occupation"
                value={beneficiary.guardian?.occupation}
              />
            </Col>
            <Col xs={12} sm={6}>
              <InfoRow
                label="Monthly Income Range"
                value={beneficiary.guardian?.monthly_income_range}
              />
            </Col>
            <Col xs={12}>
              <InfoRow label="Address" value={beneficiary.guardian?.address} />
            </Col>
            <Col xs={12}>
              <InfoRow
                label="Additional Notes"
                value={beneficiary.guardian?.notes}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Next of Kin Card */}
      {beneficiary.next_of_kin && (
        <Card className="border shadow-none mb-3">
          <Card.Header className="pb-0">
            <Card.Title as="h5" className="pt-2">
              Next of Kin
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <InfoRow
                  label="First Name"
                  value={beneficiary.next_of_kin.first_name}
                />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Middle Name"
                  value={beneficiary.next_of_kin.middle_name}
                />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Last Name"
                  value={beneficiary.next_of_kin.last_name}
                />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Relationship"
                  value={beneficiary.next_of_kin.relationship}
                />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Contact Number"
                  value={beneficiary.next_of_kin.phone}
                />
              </Col>
              <Col xs={12} sm={6}>
                <InfoRow
                  label="Contact Email"
                  value={beneficiary.next_of_kin.email}
                />
              </Col>
              <Col xs={12}>
                <InfoRow
                  label="Address"
                  value={beneficiary.next_of_kin.address}
                />
              </Col>
              <Col xs={12}>
                <InfoRow label="Notes" value={beneficiary.next_of_kin.notes} />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Status Change History Card */}
      <StatusChangeHistory beneficiaryId={beneficiary.id} />
    </>
  );
};

// Status Change History Component
const StatusChangeHistory = ({ beneficiaryId }) => {
  const { data: logs, isLoading } = useBeneficiaryStatusLogs(beneficiaryId);

  const formatDateTime = dateString => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy hh:mm a');
    } catch {
      return '-';
    }
  };

  const getFieldLabel = fieldName => {
    switch (fieldName) {
      case 'sponsorship_status':
        return 'Sponsorship Status';
      case 'priority_status':
        return 'Priority Status';
      case 'sponsorship_payment':
        return 'Sponsorship Payment';
      default:
        return fieldName;
    }
  };

  return (
    <Card className="border shadow-none mb-3">
      <Card.Header className="pb-0">
        <Card.Title as="h5" className="pt-2">
          Status Change History
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : logs && logs.length > 0 ? (
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Field</th>
                <th>Old Value</th>
                <th>New Value</th>
                <th>Changed By</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td className="text-nowrap">
                    {formatDateTime(log.created_at)}
                  </td>
                  <td>{getFieldLabel(log.field_name)}</td>
                  <td>
                    {log.old_value ? (
                      <SoftBadge pill bg="secondary">
                        {log.old_value}
                      </SoftBadge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {log.new_value ? (
                      <SoftBadge pill bg="primary">
                        {log.new_value}
                      </SoftBadge>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{log.changed_by_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted mb-0">No status changes recorded</p>
        )}
      </Card.Body>
    </Card>
  );
};

export default BeneficiaryProfile;
